import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Package,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  ArrowLeftRight,
  Users,
  TrendingUp,
  Calendar,
  Settings,
  Eye,
  EyeOff,
  Wifi,
  WifiOff,
  Handshake,
  Wrench,
  Palette,
  ChevronDown,
  Download,
  Loader2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { formatDate, getMovementTypeLabel, getMovementTypeColor } from '../lib/utils';
import { toast } from 'sonner';

const COLORS = ['#00B2A9', '#10B981', '#3B82F6', '#F59E0B', '#EF4444'];

const DEFAULT_CHART_COLORS = {
  palette: ['#00B2A9', '#10B981', '#3B82F6', '#F59E0B', '#EF4444'],
  entradas: '#10B981',
  salidas: '#EF4444',
  retornos: '#3B82F6',
  barPrimary: '#00B2A9',
  barSecondary: '#3B82F6',
  pieFill: '#8884d8'
};

const COLOR_PRESETS = [
  { name: 'Default', colors: { palette: ['#00B2A9', '#10B981', '#3B82F6', '#F59E0B', '#EF4444'], entradas: '#10B981', salidas: '#EF4444', retornos: '#3B82F6', barPrimary: '#00B2A9', barSecondary: '#3B82F6', pieFill: '#8884d8' }},
  { name: 'Oceano', colors: { palette: ['#0EA5E9', '#06B6D4', '#14B8A6', '#0284C7', '#38BDF8'], entradas: '#06B6D4', salidas: '#0284C7', retornos: '#14B8A6', barPrimary: '#0EA5E9', barSecondary: '#38BDF8', pieFill: '#0EA5E9' }},
  { name: 'Bosque', colors: { palette: ['#059669', '#16A34A', '#65A30D', '#84CC16', '#22C55E'], entradas: '#16A34A', salidas: '#65A30D', retornos: '#059669', barPrimary: '#059669', barSecondary: '#22C55E', pieFill: '#16A34A' }},
  { name: 'Atardecer', colors: { palette: ['#F97316', '#EF4444', '#EC4899', '#F59E0B', '#DC2626'], entradas: '#F97316', salidas: '#EF4444', retornos: '#EC4899', barPrimary: '#F97316', barSecondary: '#F59E0B', pieFill: '#EC4899' }},
  { name: 'Corporativo', colors: { palette: ['#1E40AF', '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7'], entradas: '#3B82F6', salidas: '#6366F1', retornos: '#1E40AF', barPrimary: '#1E40AF', barSecondary: '#8B5CF6', pieFill: '#6366F1' }},
  { name: 'Neon', colors: { palette: ['#22D3EE', '#A3E635', '#F472B6', '#FACC15', '#FB923C'], entradas: '#A3E635', salidas: '#F472B6', retornos: '#22D3EE', barPrimary: '#22D3EE', barSecondary: '#FACC15', pieFill: '#A3E635' }},
];

const DEFAULT_WIDGETS = {
  kpis: true,
  kpis_secondary: true,
  equipmentByStatus: true,
  movementsByMonth: true,
  topEquipment: true,
  recentMovements: true,
  upcomingEvents: true,
  // New widgets
  upcomingReservations: true,
  overdueMaintenance: true,
  activeFaults: true,
  equipmentByLocation: false,
  equipmentByCategory: false,
  pendingReturns: true,
  lowStockAlerts: false,
  movementsTrend: false
};

const DashboardPage = () => {
  const { api, isAdmin, isSuperAdmin } = useAuth();
  const { isConnected, lastMessage } = useWebSocket();
  const navigate = useNavigate();
  
  // Loading states - separate for primary and secondary data
  const [loadingPrimary, setLoadingPrimary] = useState(true);
  const [loadingSecondary, setLoadingSecondary] = useState(true);
  
  // Primary widget data (always loaded first)
  const [kpis, setKpis] = useState(null);
  const [equipmentByStatus, setEquipmentByStatus] = useState(null);
  const [movementsByMonth, setMovementsByMonth] = useState(null);
  const [topEquipment, setTopEquipment] = useState(null);
  const [recentMovements, setRecentMovements] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingReservations, setUpcomingReservations] = useState([]);
  const [overdueMaintenance, setOverdueMaintenance] = useState([]);
  const [activeFaults, setActiveFaults] = useState([]);
  const [resolvingFaults, setResolvingFaults] = useState(false);
  
  // Secondary widget data (loaded on demand)
  const [equipmentByLocation, setEquipmentByLocation] = useState([]);
  const [equipmentByCategory, setEquipmentByCategory] = useState([]);
  const [pendingReturns, setPendingReturns] = useState({ items: [], total: 0, critical_count: 0, warning_count: 0 });
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [movementsTrend, setMovementsTrend] = useState([]);
  
  // Cache timestamp to avoid unnecessary reloads (ref to prevent re-render cycles)
  const lastLoadTimeRef = React.useRef(null);
  const CACHE_DURATION = 30000; // 30 seconds cache
  
  // Widget visibility state
  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem('dashboard_widgets');
    return saved ? JSON.parse(saved) : DEFAULT_WIDGETS;
  });

  // Chart color customization
  const [chartColors, setChartColors] = useState(() => {
    const saved = localStorage.getItem('dashboard_chart_colors');
    return saved ? JSON.parse(saved) : DEFAULT_CHART_COLORS;
  });
  const [showColorSection, setShowColorSection] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ threshold_days: 7, warning_days: 3, enabled: true });
  const [showAlertConfig, setShowAlertConfig] = useState(false);
  // v5.3.5: lost-overdue config + movement transition notifications config
  const [lostOverdueConfig, setLostOverdueConfig] = useState({
    enabled: true, threshold_days: 15, send_email: true, notify_user_ids: [],
  });
  const [showLostOverdueConfig, setShowLostOverdueConfig] = useState(false);
  const [transitionAlertsConfig, setTransitionAlertsConfig] = useState({
    enabled: true, send_email: true, notify_user_ids: [],
    email_subject_template: '', email_body_template: '',
  });
  const [showTransitionConfig, setShowTransitionConfig] = useState(false);
  const [transitionPreview, setTransitionPreview] = useState(null);
  // v5.3.7: which step tab is active in the per-step template editor
  const [activeTemplateStep, setActiveTemplateStep] = useState('entregado');
  // v5.3.7: ad-hoc transitions report (days selector)
  const [reportDays, setReportDays] = useState(7);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showReports, setShowReports] = useState(false);
  // v5.3.8: executive KPIs widget state
  const [execKpis, setExecKpis] = useState(null);
  const [execKpisDays, setExecKpisDays] = useState(30);
  const [execKpisLoading, setExecKpisLoading] = useState(false);
  // v5.3.9: KPI threshold alerts config
  const [kpiThresholdConfig, setKpiThresholdConfig] = useState({
    enabled: false, hour: 9, window_days: 7,
    max_avg_cycle_hours: 72, min_completion_rate: 70, min_notes_percent: 50,
    notify_user_ids: [], send_email: true,
    send_teams_webhook: false, teams_webhook_url: '',
  });
  const [showKpiThresholdConfig, setShowKpiThresholdConfig] = useState(false);
  const [teamsTestLoading, setTeamsTestLoading] = useState(false);
  // v5.3.6: categories list for per-category lost-overdue thresholds
  const [allCategories, setAllCategories] = useState([]);

  // Load primary data (KPIs and main charts) - always loaded
  const loadPrimaryData = useCallback(async (forceRefresh = false) => {
    // Check cache - skip if data was loaded recently
    if (!forceRefresh && lastLoadTimeRef.current && Date.now() - lastLoadTimeRef.current < CACHE_DURATION) {
      return;
    }
    
    setLoadingPrimary(true);
    try {
      const apiInstance = api();
      
      // Build request array based on visible widgets for better performance
      const requests = [];
      const requestKeys = [];
      
      // Always load KPIs (needed for primary and secondary KPI cards)
      requests.push(apiInstance.get('/dashboard/kpis'));
      requestKeys.push('kpis');
      
      // Load chart data only if widgets are visible
      if (widgets.equipmentByStatus) {
        requests.push(apiInstance.get('/dashboard/equipment-by-status'));
        requestKeys.push('status');
      }
      if (widgets.movementsByMonth) {
        requests.push(apiInstance.get('/dashboard/movements-by-month'));
        requestKeys.push('month');
      }
      if (widgets.topEquipment) {
        requests.push(apiInstance.get('/dashboard/top-equipment'));
        requestKeys.push('top');
      }
      if (widgets.recentMovements) {
        requests.push(apiInstance.get('/dashboard/recent-movements'));
        requestKeys.push('recent');
      }
      if (widgets.upcomingEvents) {
        requests.push(apiInstance.get('/dashboard/upcoming-events'));
        requestKeys.push('events');
      }
      if (widgets.upcomingReservations) {
        requests.push(apiInstance.get('/dashboard/upcoming-reservations'));
        requestKeys.push('reservations');
      }
      if (widgets.overdueMaintenance) {
        requests.push(apiInstance.get('/dashboard/overdue-maintenance'));
        requestKeys.push('maintenance');
      }
      if (widgets.activeFaults) {
        requests.push(apiInstance.get('/dashboard/active-faults'));
        requestKeys.push('faults');
      }
      if (widgets.pendingReturns) {
        requests.push(apiInstance.get('/dashboard/pending-returns').catch(() => ({ data: { items: [], total: 0, critical_count: 0, warning_count: 0 } })));
        requestKeys.push('pending');
      }
      
      const results = await Promise.all(requests);
      
      // Map results to state
      results.forEach((res, idx) => {
        const key = requestKeys[idx];
        switch (key) {
          case 'kpis': setKpis(res.data); break;
          case 'status': setEquipmentByStatus(res.data); break;
          case 'month': setMovementsByMonth(res.data); break;
          case 'top': setTopEquipment(res.data); break;
          case 'recent': setRecentMovements(res.data); break;
          case 'events': setUpcomingEvents(res.data); break;
          case 'reservations': setUpcomingReservations(res.data); break;
          case 'maintenance': setOverdueMaintenance(res.data); break;
          case 'faults': setActiveFaults(res.data); break;
          case 'pending': setPendingReturns(res.data || { items: [], total: 0 }); break;
          default: break;
        }
      });
      
      lastLoadTimeRef.current = Date.now();
    } catch (error) {
      console.error('Error loading primary dashboard data:', error);
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoadingPrimary(false);
    }
  }, [api, widgets]);

  // Load secondary data (optional widgets) - loaded after primary
  const loadSecondaryData = useCallback(async () => {
    // Only load if any secondary widget is visible
    if (!widgets.equipmentByLocation && !widgets.equipmentByCategory) {
      setLoadingSecondary(false);
      return;
    }
    
    setLoadingSecondary(true);
    try {
      const apiInstance = api();
      const requests = [];
      const requestKeys = [];
      
      if (widgets.equipmentByLocation) {
        requests.push(apiInstance.get('/dashboard/equipment-by-location').catch(() => ({ data: [] })));
        requestKeys.push('location');
      }
      if (widgets.equipmentByCategory) {
        requests.push(apiInstance.get('/dashboard/equipment-by-category').catch(() => ({ data: [] })));
        requestKeys.push('category');
      }
      if (widgets.lowStockAlerts) {
        requests.push(apiInstance.get('/stock-alerts/status').catch(() => ({ data: { low_stock_items: [] } })));
        requestKeys.push('lowstock');
      }
      
      if (requests.length > 0) {
        const results = await Promise.all(requests);
        results.forEach((res, idx) => {
          const key = requestKeys[idx];
          switch (key) {
            case 'location': setEquipmentByLocation(res.data || []); break;
            case 'category': setEquipmentByCategory(res.data || []); break;
            case 'lowstock': setLowStockAlerts(res.data?.low_stock_items || []); break;
            default: break;
          }
        });
      }
    } catch (error) {
      console.error('Error loading secondary dashboard data:', error);
    } finally {
      setLoadingSecondary(false);
    }
  }, [api, widgets]);

  // Combined loading state for backward compatibility
  const loading = loadingPrimary;

  // Load data on mount - primary first, then secondary
  useEffect(() => {
    loadPrimaryData();
  }, [loadPrimaryData]);

  // Load secondary data after primary completes
  useEffect(() => {
    if (!loadingPrimary) {
      loadSecondaryData();
    }
  }, [loadingPrimary, loadSecondaryData]);

  // Save widget preferences
  useEffect(() => {
    localStorage.setItem('dashboard_widgets', JSON.stringify(widgets));
  }, [widgets]);

  // Save chart color preferences
  useEffect(() => {
    localStorage.setItem('dashboard_chart_colors', JSON.stringify(chartColors));
  }, [chartColors]);

  // Load return alert config for admin users
  useEffect(() => {
    if (isAdmin() || isSuperAdmin()) {
      api().get('/config/return-alerts').then(res => setAlertConfig(res.data)).catch(() => {});
      // v5.3.5: also load lost-overdue & transition alerts configs
      api().get('/config/lost-overdue-alerts').then(res => setLostOverdueConfig(res.data)).catch(() => {});
      api().get('/config/movement-transition-alerts').then(res => setTransitionAlertsConfig(res.data)).catch(() => {});
      // v5.3.6: load categories for per-category thresholds
      api().get('/categories').then(res => setAllCategories(res.data || [])).catch(() => {});
      // v5.3.9: load KPI threshold alerts config
      api().get('/config/kpi-threshold-alerts').then(res => setKpiThresholdConfig(res.data)).catch(() => {});
    }
  }, [api, isAdmin, isSuperAdmin]);

  // v5.3.8: load executive KPIs (admin only)
  const loadExecKpis = useCallback(async () => {
    if (!(isAdmin() || isSuperAdmin())) return;
    setExecKpisLoading(true);
    try {
      const res = await api().get(`/reports/executive-kpis?days=${execKpisDays}`);
      setExecKpis(res.data);
    } catch (err) {
      // silent — widget is best-effort
    } finally {
      setExecKpisLoading(false);
    }
  }, [api, isAdmin, isSuperAdmin, execKpisDays]);

  useEffect(() => { loadExecKpis(); }, [loadExecKpis]);

  const saveAlertConfig = async (newConfig) => {
    try {
      const res = await api().put('/config/return-alerts', newConfig);
      setAlertConfig(res.data.settings || newConfig);
      toast.success('Configuración de alertas guardada');
      loadPrimaryData(true);
    } catch (err) {
      toast.error('Error al guardar configuración');
    }
  };

  // v5.3.5: save lost-overdue alerts config
  const saveLostOverdueConfig = async (newConfig) => {
    try {
      const res = await api().put('/config/lost-overdue-alerts', newConfig);
      setLostOverdueConfig(res.data.settings || newConfig);
      toast.success('Alertas de pérdida actualizadas');
    } catch (err) {
      toast.error('Error al guardar alertas de pérdida');
    }
  };

  // v5.3.5: save movement transition alerts config
  const saveTransitionAlertsConfig = async (newConfig) => {
    try {
      const res = await api().put('/config/movement-transition-alerts', newConfig);
      setTransitionAlertsConfig(res.data.settings || newConfig);
      toast.success('Notificaciones de transiciones actualizadas');
    } catch (err) {
      toast.error('Error al guardar configuración');
    }
  };

  // WebSocket real-time updates - force refresh when events occur
  useEffect(() => {
    if (lastMessage) {
      const refreshTypes = ['movement_created', 'movement_updated', 'unit_updated', 'inventory_updated'];
      if (refreshTypes.includes(lastMessage.type)) {
        // Force refresh dashboard data when relevant events occur
        loadPrimaryData(true);
        loadSecondaryData();
      }
    }
  }, [lastMessage, loadPrimaryData, loadSecondaryData]);

  // Reload secondary data when secondary widgets are toggled on
  useEffect(() => {
    const needsLocationData = widgets.equipmentByLocation && equipmentByLocation.length === 0;
    const needsCategoryData = widgets.equipmentByCategory && equipmentByCategory.length === 0;
    const needsLowStockData = widgets.lowStockAlerts && lowStockAlerts.length === 0;
    
    if ((needsLocationData || needsCategoryData || needsLowStockData) && !loadingSecondary) {
      loadSecondaryData();
    }
  }, [widgets.equipmentByLocation, widgets.equipmentByCategory, widgets.lowStockAlerts,
      equipmentByLocation.length, equipmentByCategory.length, lowStockAlerts.length,
      loadSecondaryData, loadingSecondary]);

  const toggleWidget = (key) => {
    setWidgets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resetWidgets = () => {
    setWidgets(DEFAULT_WIDGETS);
    toast.success('Widgets restaurados');
  };

  const applyColorPreset = (preset) => {
    setChartColors(preset.colors);
    toast.success(`Paleta "${preset.name}" aplicada`);
  };

  const updateChartColor = (key, color) => {
    setChartColors(prev => ({ ...prev, [key]: color }));
  };

  const updatePaletteColor = (index, color) => {
    setChartColors(prev => {
      const newPalette = [...prev.palette];
      newPalette[index] = color;
      return { ...prev, palette: newPalette };
    });
  };

  const resetColors = () => {
    setChartColors(DEFAULT_CHART_COLORS);
    toast.success('Colores restaurados');
  };

  // Navigate to inventory with status filter
  const navigateToInventory = (status) => {
    if (status) {
      navigate(`/inventory?status=${status}`);
    } else {
      navigate('/inventory');
    }
  };

  const KPICard = ({ icon: Icon, label, value, color, loading: isLoading, onClick, clickable = false }) => (
    <Card 
      className={`kpi-card ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-primary/50 active:ring-2 active:ring-primary/70 active:scale-[0.98] transition-all select-none' : ''}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div className={`kpi-icon ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
      {isLoading ? (
        <Skeleton className="h-10 w-24 mt-4" />
      ) : (
        <div className="kpi-value">{value?.toLocaleString() || 0}</div>
      )}
      <div className="kpi-label">{label}</div>
    </Card>
  );

  const statusChartData = equipmentByStatus ? 
    equipmentByStatus.labels.map((label, idx) => ({
      name: label,
      value: equipmentByStatus.data[idx]
    })) : [];

  const monthChartData = movementsByMonth ?
    movementsByMonth.labels.map((label, idx) => ({
      month: label,
      entradas: movementsByMonth.datasets[0]?.data[idx] || 0,
      salidas: movementsByMonth.datasets[1]?.data[idx] || 0,
      retornos: movementsByMonth.datasets[2]?.data[idx] || 0
    })) : [];

  const topEquipmentData = topEquipment ?
    topEquipment.labels.map((label, idx) => ({
      name: label,
      uso: topEquipment.data[idx]
    })) : [];

  const widgetConfig = [
    { key: 'kpis', label: 'KPIs Principales', description: 'Total, Disponibles, Asignados, Stock Bajo' },
    { key: 'kpis_secondary', label: 'KPIs Secundarios', description: 'Mantenimiento, Fallas, Movimientos, Utilización' },
    { key: 'equipmentByStatus', label: 'Equipos por Estado', description: 'Gráfico circular de estados' },
    { key: 'movementsByMonth', label: 'Movimientos por Mes', description: 'Gráfico de líneas temporal' },
    { key: 'topEquipment', label: 'Top Equipos', description: 'Los 10 más utilizados' },
    { key: 'recentMovements', label: 'Últimos Movimientos', description: 'Actividad reciente' },
    { key: 'upcomingEvents', label: 'Próximos Eventos', description: 'Calendario de eventos' },
    { key: 'upcomingReservations', label: 'Reservas Próximas', description: 'Reservas de la semana' },
    { key: 'overdueMaintenance', label: 'Mantenimiento Vencido', description: 'Mantenimientos pendientes o vencidos' },
    { key: 'activeFaults', label: 'Fallas Activas', description: 'Unidades con falla activa y acción rápida' },
    // New widgets
    { key: 'equipmentByLocation', label: 'Equipos por Ubicación', description: 'Distribución geográfica de equipos' },
    { key: 'equipmentByCategory', label: 'Equipos por Categoría', description: 'Distribución por tipo de equipo' },
    { key: 'pendingReturns', label: 'Pendientes de Retorno', description: 'Equipos que aún no regresan' },
    { key: 'lowStockAlerts', label: 'Alertas Stock Bajo', description: 'Equipos con pocas unidades disponibles' }
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            Resumen general del inventario
            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${isConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {isConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {isConnected ? 'En vivo' : 'Desconectado'}
            </span>
          </p>
        </div>
        
        {/* Settings Sheet */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" data-testid="dashboard-settings-btn">
              <Settings className="h-4 w-4 mr-2" />
              Personalizar
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Personalizar Dashboard</SheetTitle>
              <SheetDescription>
                Elige qué widgets mostrar en tu dashboard
              </SheetDescription>
            </SheetHeader>
            <div className="py-6 space-y-6 max-h-[70vh] overflow-y-auto pr-2">
              {widgetConfig.map(({ key, label, description }) => (
                <div key={key} className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor={key} className="text-sm font-medium">{label}</Label>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <Switch
                    id={key}
                    checked={widgets[key]}
                    onCheckedChange={() => toggleWidget(key)}
                  />
                </div>
              ))}
            </div>
            <div className="pt-4 border-t space-y-4">
              {/* Color Customization Section */}
              <button
                onClick={() => setShowColorSection(!showColorSection)}
                className="w-full flex items-center justify-between text-sm font-medium hover:text-primary transition-colors"
                data-testid="toggle-color-section"
              >
                <span className="flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Personalizar Colores
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showColorSection ? 'rotate-180' : ''}`} />
              </button>

              {showColorSection && (
                <div className="space-y-4 animate-in slide-in-from-top-2">
                  {/* Presets */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Paletas predefinidas</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {COLOR_PRESETS.map(preset => (
                        <button
                          key={preset.name}
                          onClick={() => applyColorPreset(preset)}
                          className="flex items-center gap-2 p-2 border rounded-md hover:bg-muted/50 transition-colors text-left"
                          data-testid={`color-preset-${preset.name.toLowerCase()}`}
                        >
                          <div className="flex gap-0.5">
                            {preset.colors.palette.map((c, i) => (
                              <div key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />
                            ))}
                          </div>
                          <span className="text-xs">{preset.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Individual Colors */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Colores individuales</Label>
                    <div className="space-y-2">
                      {[
                        { key: 'entradas', label: 'Entradas' },
                        { key: 'salidas', label: 'Salidas' },
                        { key: 'retornos', label: 'Retornos' },
                        { key: 'barPrimary', label: 'Barras principales' },
                        { key: 'barSecondary', label: 'Barras secundarias' },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between gap-2">
                          <span className="text-xs">{label}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded border" style={{ backgroundColor: chartColors[key] }} />
                            <input
                              type="color"
                              value={chartColors[key]}
                              onChange={e => updateChartColor(key, e.target.value)}
                              className="w-8 h-6 rounded cursor-pointer border-0"
                              data-testid={`color-picker-${key}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Palette Colors */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Paleta de graficas de pastel</Label>
                    <div className="flex items-center gap-2">
                      {chartColors.palette.map((color, i) => (
                        <div key={i} className="flex flex-col items-center gap-1">
                          <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color }} />
                          <input
                            type="color"
                            value={color}
                            onChange={e => updatePaletteColor(i, e.target.value)}
                            className="w-6 h-4 rounded cursor-pointer border-0"
                            data-testid={`palette-color-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Return Alert Config - Admin only */}
              {(isAdmin() || isSuperAdmin()) && (
                <>
                  <button
                    onClick={() => setShowAlertConfig(!showAlertConfig)}
                    className="w-full flex items-center justify-between text-sm font-medium hover:text-primary transition-colors"
                    data-testid="toggle-alert-config"
                  >
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Alertas de Retorno
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showAlertConfig ? 'rotate-180' : ''}`} />
                  </button>

                  {showAlertConfig && (
                    <div className="space-y-3 animate-in slide-in-from-top-2 p-3 bg-muted/30 rounded-lg">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Días para alerta crítica (rojo)</Label>
                        <input
                          type="number"
                          min="1"
                          max="90"
                          value={alertConfig.threshold_days}
                          onChange={e => setAlertConfig(prev => ({ ...prev, threshold_days: parseInt(e.target.value) || 7 }))}
                          className="w-full h-8 px-2 text-sm rounded border bg-background"
                          data-testid="alert-threshold-input"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Días para advertencia (amarillo)</Label>
                        <input
                          type="number"
                          min="1"
                          max="90"
                          value={alertConfig.warning_days}
                          onChange={e => setAlertConfig(prev => ({ ...prev, warning_days: parseInt(e.target.value) || 3 }))}
                          className="w-full h-8 px-2 text-sm rounded border bg-background"
                          data-testid="alert-warning-input"
                        />
                      </div>
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => saveAlertConfig(alertConfig)}
                        data-testid="save-alert-config-btn"
                      >
                        Guardar Alertas
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={async () => {
                          try {
                            await api().post('/config/return-alerts/test');
                            toast.success('Verificación ejecutada. Revisa tu email.');
                          } catch { toast.error('Error al enviar test'); }
                        }}
                        data-testid="test-alert-email-btn"
                      >
                        Enviar Email de Prueba
                      </Button>
                    </div>
                  )}

                  {/* v5.3.5: Lost-overdue equipment alerts (perdido_recuperar > X días) */}
                  <button
                    onClick={() => setShowLostOverdueConfig(!showLostOverdueConfig)}
                    className="w-full flex items-center justify-between text-sm font-medium hover:text-primary transition-colors"
                    data-testid="toggle-lost-overdue-config"
                  >
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-rose-500" />
                      Alertas de Equipo Perdido (P/R)
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showLostOverdueConfig ? 'rotate-180' : ''}`} />
                  </button>
                  {showLostOverdueConfig && (
                    <div className="space-y-3 animate-in slide-in-from-top-2 p-3 bg-muted/30 rounded-lg" data-testid="lost-overdue-config-panel">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Notifica a admins cuando un equipo lleva más de N días en estado{' '}
                        <b className="text-rose-400">Perdido a Recuperar</b>. La verificación corre cada hora.
                      </p>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          data-testid="lost-overdue-enabled"
                          checked={!!lostOverdueConfig.enabled}
                          onChange={e => setLostOverdueConfig(p => ({ ...p, enabled: e.target.checked }))}
                          className="rounded border"
                        />
                        Activar alertas
                      </label>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Umbral (días)</Label>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={lostOverdueConfig.threshold_days}
                          onChange={e => setLostOverdueConfig(p => ({ ...p, threshold_days: parseInt(e.target.value) || 15 }))}
                          className="w-full h-8 px-2 text-sm rounded border bg-background"
                          data-testid="lost-overdue-threshold"
                        />
                      </div>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          data-testid="lost-overdue-send-email"
                          checked={!!lostOverdueConfig.send_email}
                          onChange={e => setLostOverdueConfig(p => ({ ...p, send_email: e.target.checked }))}
                          className="rounded border"
                        />
                        Enviar correo además de notificación in-app
                      </label>
                      <Button
                        size="sm"
                        className="w-full"
                        data-testid="save-lost-overdue-btn"
                        onClick={() => saveLostOverdueConfig(lostOverdueConfig)}
                      >
                        Guardar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        data-testid="test-lost-overdue-btn"
                        onClick={async () => {
                          try {
                            await api().post('/config/lost-overdue-alerts/test');
                            toast.success('Verificación ejecutada. Revisa la campanita.');
                          } catch { toast.error('Error al ejecutar test'); }
                        }}
                      >
                        Ejecutar ahora
                      </Button>

                      {/* v5.3.6: per-category threshold overrides */}
                      <details className="mt-1 group" data-testid="lost-overdue-per-category">
                        <summary className="cursor-pointer text-xs font-medium text-primary hover:underline list-none flex items-center gap-1">
                          <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
                          Umbral por categoría (opcional)
                        </summary>
                        <div className="mt-2 space-y-2">
                          <p className="text-[11px] text-muted-foreground">
                            Sobrescribe el umbral global por categoría. Las que no estén aquí usarán el umbral global.
                          </p>
                          <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                            {allCategories.length === 0 && (
                              <p className="text-[11px] text-muted-foreground italic">Sin categorías cargadas.</p>
                            )}
                            {allCategories.map(cat => {
                              const current = (lostOverdueConfig.category_thresholds || {})[cat.id];
                              return (
                                <div key={cat.id} className="flex items-center gap-2 text-xs"
                                     data-testid={`cat-threshold-row-${cat.id}`}>
                                  <span className="flex-1 truncate" title={cat.name}>{cat.name}</span>
                                  <input
                                    type="number"
                                    min="1"
                                    max="365"
                                    placeholder="global"
                                    value={current ?? ''}
                                    onChange={e => {
                                      const val = e.target.value;
                                      setLostOverdueConfig(prev => {
                                        const next = { ...(prev.category_thresholds || {}) };
                                        if (val === '' || val === '0') {
                                          delete next[cat.id];
                                        } else {
                                          next[cat.id] = Math.max(1, Math.min(365, parseInt(val) || 15));
                                        }
                                        return { ...prev, category_thresholds: next };
                                      });
                                    }}
                                    className="w-16 h-7 px-1.5 text-xs rounded border bg-background text-right"
                                    data-testid={`cat-threshold-input-${cat.id}`}
                                  />
                                  <span className="text-[10px] text-muted-foreground w-5">d</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </details>
                    </div>
                  )}

                  {/* v5.3.5: Movement transition notifications */}
                  <button
                    onClick={() => setShowTransitionConfig(!showTransitionConfig)}
                    className="w-full flex items-center justify-between text-sm font-medium hover:text-primary transition-colors"
                    data-testid="toggle-transition-config"
                  >
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-sky-500" />
                      Notificaciones de Transiciones
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showTransitionConfig ? 'rotate-180' : ''}`} />
                  </button>
                  {showTransitionConfig && (
                    <div className="space-y-3 animate-in slide-in-from-top-2 p-3 bg-muted/30 rounded-lg" data-testid="transition-config-panel">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Notifica a admins en cada paso del workflow de salidas:{' '}
                        <b>Salida → Entregado → Retorno → Completado</b>.
                      </p>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          data-testid="transition-enabled"
                          checked={!!transitionAlertsConfig.enabled}
                          onChange={e => setTransitionAlertsConfig(p => ({ ...p, enabled: e.target.checked }))}
                          className="rounded border"
                        />
                        Activar notificaciones
                      </label>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          data-testid="transition-send-email"
                          checked={!!transitionAlertsConfig.send_email}
                          onChange={e => setTransitionAlertsConfig(p => ({ ...p, send_email: e.target.checked }))}
                          className="rounded border"
                        />
                        Enviar correo además de notificación in-app
                      </label>
                      <Button
                        size="sm"
                        className="w-full"
                        data-testid="save-transition-btn"
                        onClick={() => saveTransitionAlertsConfig(transitionAlertsConfig)}
                      >
                        Guardar
                      </Button>

                      {/* v5.3.7: per-step email template editor */}
                      <details className="mt-1 group" data-testid="transition-email-template">
                        <summary className="cursor-pointer text-xs font-medium text-primary hover:underline list-none flex items-center gap-1">
                          <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
                          Plantillas de correo por paso
                        </summary>
                        <div className="mt-2 space-y-2">
                          <p className="text-[11px] text-muted-foreground leading-relaxed">
                            Placeholders:{' '}
                            <code className="text-[10px]">{'{step} {client} {event} {control} {actor} {notes} {url} {movement_id} {priority}'}</code>
                            . Si dejas un paso vacío, se usa la plantilla global o el default elegante.
                          </p>

                          {/* Step tabs */}
                          <div className="flex gap-1 border-b">
                            {[
                              { id: 'entregado',  label: 'Entregado'  },
                              { id: 'retorno',    label: 'Retorno'    },
                              { id: 'completado', label: 'Completado' },
                            ].map(t => (
                              <button
                                key={t.id}
                                type="button"
                                data-testid={`template-tab-${t.id}`}
                                onClick={() => setActiveTemplateStep(t.id)}
                                className={`px-2 py-1 text-[11px] font-medium border-b-2 transition-colors ${
                                  activeTemplateStep === t.id
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-muted-foreground hover:text-foreground'
                                }`}
                              >
                                {t.label}
                              </button>
                            ))}
                          </div>

                          {/* Step-specific subject/body */}
                          <div className="space-y-1">
                            <Label className="text-[11px]">Asunto para {activeTemplateStep}</Label>
                            <input
                              type="text"
                              data-testid={`transition-subject-${activeTemplateStep}`}
                              placeholder={`[CM Idiomas] Movimiento {step} · {client}`}
                              value={(transitionAlertsConfig.templates?.[activeTemplateStep]?.subject) || ''}
                              onChange={e => setTransitionAlertsConfig(p => ({
                                ...p,
                                templates: {
                                  ...(p.templates || {}),
                                  [activeTemplateStep]: {
                                    ...(p.templates?.[activeTemplateStep] || {}),
                                    subject: e.target.value,
                                  },
                                },
                              }))}
                              className="w-full h-8 px-2 text-xs rounded border bg-background font-mono"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[11px]">HTML del cuerpo (vacío = usa el global o default)</Label>
                            <textarea
                              data-testid={`transition-body-${activeTemplateStep}`}
                              rows={6}
                              placeholder="<p>{actor} marcó el movimiento como {step}. <a href='{url}'>Abrir</a></p>"
                              value={(transitionAlertsConfig.templates?.[activeTemplateStep]?.body) || ''}
                              onChange={e => setTransitionAlertsConfig(p => ({
                                ...p,
                                templates: {
                                  ...(p.templates || {}),
                                  [activeTemplateStep]: {
                                    ...(p.templates?.[activeTemplateStep] || {}),
                                    body: e.target.value,
                                  },
                                },
                              }))}
                              className="w-full px-2 py-1.5 text-xs rounded border bg-background font-mono"
                            />
                          </div>

                          {/* Global fallback (collapsible) */}
                          <details>
                            <summary className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">
                              Plantilla global (fallback cuando un paso está vacío)
                            </summary>
                            <div className="mt-2 space-y-1">
                              <input
                                type="text"
                                data-testid="transition-subject-template"
                                placeholder="[CM Idiomas] Movimiento {step} · {client}"
                                value={transitionAlertsConfig.email_subject_template || ''}
                                onChange={e => setTransitionAlertsConfig(p => ({ ...p, email_subject_template: e.target.value }))}
                                className="w-full h-8 px-2 text-xs rounded border bg-background font-mono"
                              />
                              <textarea
                                data-testid="transition-body-template"
                                rows={4}
                                placeholder="HTML global (placeholder fallback)"
                                value={transitionAlertsConfig.email_body_template || ''}
                                onChange={e => setTransitionAlertsConfig(p => ({ ...p, email_body_template: e.target.value }))}
                                className="w-full px-2 py-1.5 text-xs rounded border bg-background font-mono"
                              />
                            </div>
                          </details>

                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full text-xs"
                            data-testid="preview-transition-email-btn"
                            onClick={async () => {
                              try {
                                const stepTpl = transitionAlertsConfig.templates?.[activeTemplateStep] || {};
                                const { data } = await api().post('/config/movement-transition-alerts/preview', {
                                  step: activeTemplateStep,
                                  email_subject_template:
                                    stepTpl.subject || transitionAlertsConfig.email_subject_template,
                                  email_body_template:
                                    stepTpl.body || transitionAlertsConfig.email_body_template,
                                });
                                setTransitionPreview(data);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || 'Plantilla inválida');
                              }
                            }}
                          >
                            Previsualizar paso "{activeTemplateStep}"
                          </Button>
                          {transitionPreview && (
                            <div className="border rounded p-2 bg-background space-y-1" data-testid="transition-preview-box">
                              <div className="text-[10px] text-muted-foreground">Asunto</div>
                              <div className="text-xs font-mono break-all">{transitionPreview.subject}</div>
                              <div className="text-[10px] text-muted-foreground mt-2">HTML renderizado</div>
                              <div
                                className="text-xs max-h-72 overflow-y-auto bg-white text-black rounded p-2"
                                dangerouslySetInnerHTML={{ __html: transitionPreview.html }}
                              />
                            </div>
                          )}
                        </div>
                      </details>

                      {/* v5.3.7: Digest diario */}
                      <details className="mt-1 group" data-testid="transition-digest-panel">
                        <summary className="cursor-pointer text-xs font-medium text-primary hover:underline list-none flex items-center gap-1">
                          <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
                          Resumen diario (digest)
                        </summary>
                        <div className="mt-2 space-y-2">
                          <p className="text-[11px] text-muted-foreground leading-relaxed">
                            Acumula transiciones del día y envía UN solo correo a la hora elegida (CDMX). Los pasos no incluidos van como correo individual de inmediato.
                          </p>
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              data-testid="digest-enabled"
                              checked={!!transitionAlertsConfig.digest?.enabled}
                              onChange={e => setTransitionAlertsConfig(p => ({
                                ...p, digest: { ...(p.digest || {}), enabled: e.target.checked },
                              }))}
                              className="rounded border"
                            />
                            Activar digest
                          </label>
                          <div className="space-y-1">
                            <Label className="text-[11px]">Hora del envío (0-23 CDMX)</Label>
                            <input
                              type="number"
                              min="0"
                              max="23"
                              data-testid="digest-hour"
                              value={transitionAlertsConfig.digest?.hour ?? 18}
                              onChange={e => setTransitionAlertsConfig(p => ({
                                ...p, digest: { ...(p.digest || {}), hour: parseInt(e.target.value) || 18 },
                              }))}
                              className="w-full h-8 px-2 text-xs rounded border bg-background"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[11px]">Pasos incluidos en el digest</Label>
                            {['entregado', 'retorno', 'completado'].map(step => {
                              const inSet = (transitionAlertsConfig.digest?.steps || []).includes(step);
                              return (
                                <label key={step} className="flex items-center gap-2 text-xs">
                                  <input
                                    type="checkbox"
                                    data-testid={`digest-step-${step}`}
                                    checked={inSet}
                                    onChange={() => {
                                      const cur = new Set(transitionAlertsConfig.digest?.steps || []);
                                      if (inSet) cur.delete(step); else cur.add(step);
                                      setTransitionAlertsConfig(p => ({
                                        ...p, digest: { ...(p.digest || {}), steps: Array.from(cur) },
                                      }));
                                    }}
                                    className="rounded border"
                                  />
                                  {step}
                                  {step === 'completado' && (
                                    <span className="text-[10px] text-amber-400">⚠ recomendado dejar fuera</span>
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </details>
                    </div>
                  )}

                  {/* v5.3.7: Reportes ad-hoc */}
                  <button
                    onClick={() => setShowReports(!showReports)}
                    className="w-full flex items-center justify-between text-sm font-medium hover:text-primary transition-colors"
                    data-testid="toggle-reports-section"
                  >
                    <span className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-emerald-500" />
                      Reportes Ad-hoc
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showReports ? 'rotate-180' : ''}`} />
                  </button>
                  {showReports && (
                    <div className="space-y-3 animate-in slide-in-from-top-2 p-3 bg-muted/30 rounded-lg" data-testid="reports-panel">
                      <p className="text-[11px] text-muted-foreground leading-relaxed">
                        Descarga un Excel con todas las transiciones (salida, entregado, retorno, completado) de los últimos N días.
                      </p>
                      <div className="flex items-center gap-2">
                        <Label className="text-[11px] flex-shrink-0">Últimos</Label>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={reportDays}
                          onChange={e => setReportDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 7)))}
                          className="w-16 h-8 px-2 text-xs rounded border bg-background text-right"
                          data-testid="report-days-input"
                        />
                        <span className="text-[11px] text-muted-foreground">días</span>
                      </div>
                      <Button
                        size="sm"
                        className="w-full"
                        data-testid="download-transitions-report-btn"
                        disabled={generatingReport}
                        onClick={async () => {
                          setGeneratingReport(true);
                          try {
                            const res = await api().get(`/reports/transitions/excel?days=${reportDays}`, { responseType: 'blob' });
                            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `Transiciones_ultimos_${reportDays}_dias.xlsx`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                            toast.success(`Reporte de ${reportDays} días descargado`);
                          } catch (err) {
                            toast.error('Error al generar reporte');
                          } finally {
                            setGeneratingReport(false);
                          }
                        }}
                      >
                        {generatingReport ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : null}
                        Descargar Excel
                      </Button>
                    </div>
                  )}

                  {/* v5.3.9: KPI threshold alerts + MS Teams webhook */}
                  <button
                    onClick={() => setShowKpiThresholdConfig(!showKpiThresholdConfig)}
                    className="w-full flex items-center justify-between text-sm font-medium hover:text-primary transition-colors"
                    data-testid="toggle-kpi-threshold-config"
                  >
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-rose-500" />
                      Alertas Proactivas de KPI
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showKpiThresholdConfig ? 'rotate-180' : ''}`} />
                  </button>
                  {showKpiThresholdConfig && (
                    <div className="space-y-3 animate-in slide-in-from-top-2 p-3 bg-muted/30 rounded-lg" data-testid="kpi-threshold-panel">
                      <p className="text-[11px] text-muted-foreground leading-relaxed">
                        Notifica diariamente cuando un KPI cruza un umbral. Útil para detectar cuellos de botella antes de que sean visibles.
                      </p>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          data-testid="kpi-threshold-enabled"
                          checked={!!kpiThresholdConfig.enabled}
                          onChange={e => setKpiThresholdConfig(p => ({ ...p, enabled: e.target.checked }))}
                          className="rounded border"
                        />
                        Activar alertas
                      </label>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[11px]">Hora (CDMX)</Label>
                          <input type="number" min="0" max="23"
                            value={kpiThresholdConfig.hour}
                            onChange={e => setKpiThresholdConfig(p => ({ ...p, hour: parseInt(e.target.value) || 9 }))}
                            className="w-full h-8 px-2 text-xs rounded border bg-background"
                            data-testid="kpi-threshold-hour" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px]">Ventana (días)</Label>
                          <input type="number" min="1" max="365"
                            value={kpiThresholdConfig.window_days}
                            onChange={e => setKpiThresholdConfig(p => ({ ...p, window_days: parseInt(e.target.value) || 7 }))}
                            className="w-full h-8 px-2 text-xs rounded border bg-background"
                            data-testid="kpi-threshold-window" />
                        </div>
                      </div>

                      <div className="space-y-1.5 pt-2 border-t border-border/30">
                        <Label className="text-[11px] font-semibold text-foreground">Umbrales</Label>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="flex-1">Tiempo Salida→Completado &gt;</span>
                          <input type="number" min="1"
                            value={kpiThresholdConfig.max_avg_cycle_hours ?? ''}
                            onChange={e => setKpiThresholdConfig(p => ({ ...p, max_avg_cycle_hours: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                            className="w-16 h-7 px-1.5 text-xs rounded border bg-background text-right"
                            data-testid="kpi-threshold-cycle" />
                          <span className="text-[10px] text-muted-foreground">h</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="flex-1">Tasa de cierre &lt;</span>
                          <input type="number" min="0" max="100"
                            value={kpiThresholdConfig.min_completion_rate ?? ''}
                            onChange={e => setKpiThresholdConfig(p => ({ ...p, min_completion_rate: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                            className="w-16 h-7 px-1.5 text-xs rounded border bg-background text-right"
                            data-testid="kpi-threshold-completion" />
                          <span className="text-[10px] text-muted-foreground">%</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="flex-1">% con notas &lt;</span>
                          <input type="number" min="0" max="100"
                            value={kpiThresholdConfig.min_notes_percent ?? ''}
                            onChange={e => setKpiThresholdConfig(p => ({ ...p, min_notes_percent: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                            className="w-16 h-7 px-1.5 text-xs rounded border bg-background text-right"
                            data-testid="kpi-threshold-notes" />
                          <span className="text-[10px] text-muted-foreground">%</span>
                        </div>
                      </div>

                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          data-testid="kpi-threshold-email"
                          checked={!!kpiThresholdConfig.send_email}
                          onChange={e => setKpiThresholdConfig(p => ({ ...p, send_email: e.target.checked }))}
                          className="rounded border"
                        />
                        Enviar correo
                      </label>

                      {/* MS Teams webhook */}
                      <div className="space-y-1.5 pt-2 border-t border-border/30">
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            data-testid="kpi-teams-enabled"
                            checked={!!kpiThresholdConfig.send_teams_webhook}
                            onChange={e => setKpiThresholdConfig(p => ({ ...p, send_teams_webhook: e.target.checked }))}
                            className="rounded border"
                          />
                          Enviar a Microsoft Teams (webhook)
                        </label>
                        {kpiThresholdConfig.send_teams_webhook && (
                          <>
                            <input
                              type="url"
                              placeholder="https://outlook.office.com/webhook/..."
                              value={kpiThresholdConfig.teams_webhook_url || ''}
                              onChange={e => setKpiThresholdConfig(p => ({ ...p, teams_webhook_url: e.target.value }))}
                              className="w-full h-8 px-2 text-xs rounded border bg-background font-mono"
                              data-testid="kpi-teams-url"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full text-xs"
                              disabled={teamsTestLoading || !kpiThresholdConfig.teams_webhook_url}
                              data-testid="kpi-teams-test-btn"
                              onClick={async () => {
                                setTeamsTestLoading(true);
                                try {
                                  await api().post('/config/kpi-threshold-alerts/test-teams', {
                                    teams_webhook_url: kpiThresholdConfig.teams_webhook_url,
                                  });
                                  toast.success('Mensaje de prueba enviado a Teams');
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Falló el webhook');
                                } finally {
                                  setTeamsTestLoading(false);
                                }
                              }}
                            >
                              {teamsTestLoading ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : null}
                              Probar webhook
                            </Button>
                          </>
                        )}
                      </div>

                      <Button
                        size="sm"
                        className="w-full"
                        data-testid="save-kpi-threshold-btn"
                        onClick={async () => {
                          try {
                            const { data } = await api().put('/config/kpi-threshold-alerts', kpiThresholdConfig);
                            setKpiThresholdConfig(data.settings || kpiThresholdConfig);
                            toast.success('Alertas de KPI guardadas');
                          } catch (err) {
                            toast.error('Error al guardar');
                          }
                        }}
                      >
                        Guardar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        data-testid="test-kpi-threshold-btn"
                        onClick={async () => {
                          try {
                            await api().post('/config/kpi-threshold-alerts/test');
                            toast.success('Verificación ejecutada. Revisa la campanita.');
                          } catch { toast.error('Error al ejecutar test'); }
                        }}
                      >
                        Ejecutar verificación ahora
                      </Button>
                    </div>
                  )}
                </>
              )}

              <Button variant="outline" onClick={() => { resetWidgets(); resetColors(); }} className="w-full">
                Restaurar Valores por Defecto
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* KPIs Grid */}
      {widgets.kpis && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          <KPICard
            icon={Package}
            label="Total Unidades"
            value={kpis?.total_units}
            color="bg-primary"
            loading={loading}
            clickable
            onClick={() => navigateToInventory(null)}
          />
          <KPICard
            icon={CheckCircle}
            label="Disponibles"
            value={kpis?.available}
            color="bg-emerald-500"
            loading={loading}
            clickable
            onClick={() => navigateToInventory('disponible')}
          />
          <KPICard
            icon={Clock}
            label="Asignados"
            value={kpis?.assigned}
            color="bg-blue-500"
            loading={loading}
            clickable
            onClick={() => navigateToInventory('asignado')}
          />
          <KPICard
            icon={Handshake}
            label="En Préstamo"
            value={kpis?.on_loan}
            color="bg-purple-500"
            loading={loading}
            clickable
            onClick={() => navigateToInventory('en_prestamo')}
          />
          <KPICard
            icon={AlertTriangle}
            label="Stock Bajo"
            value={kpis?.low_stock}
            color="bg-amber-500"
            loading={loading}
          />
        </div>
      )}

      {/* Second Row KPIs */}
      {widgets.kpis_secondary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            icon={XCircle}
            label="En Mantenimiento"
            value={kpis?.maintenance}
            color="bg-orange-500"
            loading={loading}
            clickable
            onClick={() => navigateToInventory('mantenimiento')}
          />
          <KPICard
            icon={XCircle}
            label="Con Fallas"
            value={kpis?.faults}
            color="bg-red-500"
            loading={loading}
            clickable
            onClick={() => navigateToInventory('falla')}
          />
          <KPICard
            icon={ArrowLeftRight}
            label="Total Movimientos"
            value={kpis?.total_movements}
            color="bg-indigo-500"
            loading={loading}
          />
          <KPICard
            icon={TrendingUp}
            label="Tasa de Utilización"
            value={`${kpis?.utilization_rate || 0}%`}
            color="bg-cyan-500"
            loading={loading}
          />
        </div>
      )}

      {/* v5.3.8: Executive KPIs Widget (admin/superadmin only) */}
      {(isAdmin() || isSuperAdmin()) && (
        <Card className="border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 via-transparent to-cyan-500/5"
              data-testid="exec-kpis-widget">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  KPIs Ejecutivos · {execKpis?.window_days || execKpisDays} días
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Resumen operativo — solo visible para administradores
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Ventana</Label>
                {[7, 30, 90].map(d => (
                  <Button
                    key={d}
                    size="sm"
                    variant={execKpisDays === d ? 'default' : 'outline'}
                    className="h-7 text-xs px-2"
                    data-testid={`exec-kpis-days-${d}`}
                    onClick={() => setExecKpisDays(d)}
                  >
                    {d}d
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  data-testid="download-top-responsables-btn"
                  onClick={async () => {
                    try {
                      const res = await api().get(`/reports/top-responsables/excel?days=${execKpisDays}`, { responseType: 'blob' });
                      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Top_Responsables_${execKpisDays}d.xlsx`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('Top responsables descargado');
                    } catch { toast.error('Error al descargar'); }
                  }}
                >
                  <Download className="h-3 w-3 mr-1" />
                  Top responsables (Excel)
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {execKpisLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 w-full" />)}
              </div>
            ) : !execKpis ? (
              <p className="text-sm text-muted-foreground">Cargando…</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Tiempo promedio */}
                <div className="rounded-lg border bg-background/50 p-3" data-testid="kpi-avg-cycle-time">
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Tiempo promedio</div>
                  <div className="text-xs text-muted-foreground">Salida → Completado</div>
                  <div className="text-2xl font-bold mt-1 text-emerald-400">
                    {execKpis.avg_salida_to_completado_hours !== null
                      ? `${execKpis.avg_salida_to_completado_hours}h`
                      : '—'}
                  </div>
                  {execKpis.bottleneck_step && (
                    <p className="text-[10px] text-amber-400 mt-1">
                      Cuello: {execKpis.bottleneck_step.replace(/_/g, ' → ')} ({execKpis.bottleneck_hours}h)
                    </p>
                  )}
                </div>

                {/* % con notas */}
                <div className="rounded-lg border bg-background/50 p-3" data-testid="kpi-percent-notes">
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Documentación</div>
                  <div className="text-xs text-muted-foreground">% con notas</div>
                  <div className="text-2xl font-bold mt-1 text-cyan-400">
                    {execKpis.percent_movements_with_notes}%
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    De {execKpis.total_salidas} salidas analizadas
                  </p>
                </div>

                {/* Completion rate */}
                <div className="rounded-lg border bg-background/50 p-3" data-testid="kpi-completion-rate">
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Tasa de cierre</div>
                  <div className="text-xs text-muted-foreground">Completados / Salidas</div>
                  <div className="text-2xl font-bold mt-1 text-indigo-400">
                    {execKpis.completion_rate_percent}%
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {execKpis.completed_movements} completados · {execKpis.in_progress_movements} en curso
                  </p>
                </div>

                {/* Top 3 responsables */}
                <div className="rounded-lg border bg-background/50 p-3" data-testid="kpi-top-3">
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Top 3 responsables</div>
                  <div className="text-xs text-muted-foreground">Por # transiciones</div>
                  <div className="mt-1 space-y-0.5">
                    {(execKpis.top_responsables || []).map((r, idx) => (
                      <div key={r.name + idx} className="flex items-center justify-between text-xs"
                           data-testid={`top-responsable-${idx}`}>
                        <span className="truncate flex items-center gap-1">
                          <span className={`inline-block w-4 text-[10px] font-bold ${
                            idx === 0 ? 'text-amber-400' : idx === 1 ? 'text-slate-300' : 'text-orange-400'
                          }`}>#{idx + 1}</span>
                          {r.name}
                        </span>
                        <span className="font-mono text-muted-foreground">{r.count}</span>
                      </div>
                    ))}
                    {(execKpis.top_responsables || []).length === 0 && (
                      <p className="text-[11px] text-muted-foreground italic">Sin actividad en la ventana</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      {(widgets.equipmentByStatus || widgets.movementsByMonth) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Equipment by Status */}
          {widgets.equipmentByStatus && (
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="text-lg">Equipos por Estado</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-64 w-full" />
                ) : statusChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ cx, cy, midAngle, innerRadius, outerRadius, name, percent }) => {
                          const RADIAN = Math.PI / 180;
                          const radius = outerRadius + 25;
                          const x = cx + radius * Math.cos(-midAngle * RADIAN);
                          const y = cy + radius * Math.sin(-midAngle * RADIAN);
                          return (
                            <text x={x} y={y} fill="hsl(var(--foreground))" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12}>
                              {`${name} (${(percent * 100).toFixed(0)}%)`}
                            </text>
                          );
                        }}
                        outerRadius={90}
                        fill={chartColors.pieFill}
                        dataKey="value"
                        stroke="hsl(var(--background))"
                        strokeWidth={2}
                      >
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={chartColors.palette[index % chartColors.palette.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          color: 'hsl(var(--foreground))'
                        }}
                        itemStyle={{ color: 'hsl(var(--foreground))' }}
                      />
                      <Legend 
                        wrapperStyle={{ fontSize: '12px', color: 'hsl(var(--foreground))' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No hay datos disponibles
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Movements by Month */}
          {widgets.movementsByMonth && (
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="text-lg">Movimientos por Mes</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-64 w-full" />
                ) : monthChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={monthChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          color: 'hsl(var(--foreground))'
                        }}
                        itemStyle={{ color: 'hsl(var(--foreground))' }}
                        labelStyle={{ color: 'hsl(var(--foreground))' }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="entradas" stroke={chartColors.entradas} strokeWidth={2} dot={{ fill: chartColors.entradas }} />
                      <Line type="monotone" dataKey="salidas" stroke={chartColors.salidas} strokeWidth={2} dot={{ fill: chartColors.salidas }} />
                      <Line type="monotone" dataKey="retornos" stroke={chartColors.retornos} strokeWidth={2} dot={{ fill: chartColors.retornos }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No hay datos disponibles
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Top Equipment */}
      {widgets.topEquipment && (
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="text-lg">Top 10 Equipos Más Usados</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : topEquipmentData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topEquipmentData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis dataKey="name" type="category" width={150} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      color: 'hsl(var(--foreground))'
                    }}
                    itemStyle={{ color: 'hsl(var(--foreground))' }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Bar dataKey="uso" fill={chartColors.barPrimary} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No hay datos disponibles
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Movements & Upcoming Events */}
      {(widgets.recentMovements || widgets.upcomingEvents) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Movements */}
          {widgets.recentMovements && (
            <Card className="card-hover">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowLeftRight className="h-5 w-5 text-primary" />
                  Últimos Movimientos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : recentMovements.length > 0 ? (
                  <div className="space-y-4">
                    {recentMovements.map((mov) => (
                      <div key={mov.id} className="flex items-start justify-between p-3 rounded-lg bg-muted/30 border border-border/40">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge className={getMovementTypeColor(mov.type)}>
                              {getMovementTypeLabel(mov.type)}
                            </Badge>
                            <span className="text-sm font-medium">{mov.responsible}</span>
                          </div>
                          <p className="text-sm text-muted-foreground truncate max-w-[250px]">
                            {mov.equipment}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(mov.date)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay movimientos recientes
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Upcoming Events */}
          {widgets.upcomingEvents && (
            <Card className="card-hover">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Próximos Eventos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : upcomingEvents.length > 0 ? (
                  <div className="space-y-4">
                    {upcomingEvents.map((evt) => (
                      <div key={evt.id} className="flex items-start justify-between p-3 rounded-lg bg-muted/30 border border-border/40">
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{evt.title}</p>
                          <p className="text-sm text-muted-foreground truncate max-w-[250px]">
                            {evt.description}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(evt.date)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay eventos programados
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* New Widgets Section - Equipment by Location and Category */}
      {(widgets.equipmentByLocation || widgets.equipmentByCategory) && (
        <div className="grid gap-6 md:grid-cols-2">
          {widgets.equipmentByLocation && (
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="text-lg">Equipos por Ubicación</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-64 w-full" />
                ) : equipmentByLocation.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={equipmentByLocation}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill={chartColors.pieFill}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {equipmentByLocation.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={chartColors.palette[index % chartColors.palette.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          color: 'hsl(var(--foreground))'
                        }}
                        itemStyle={{ color: 'hsl(var(--foreground))' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No hay datos disponibles
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {widgets.equipmentByCategory && (
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="text-lg">Equipos por Categoría</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-64 w-full" />
                ) : equipmentByCategory.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={equipmentByCategory}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          color: 'hsl(var(--foreground))'
                        }}
                        itemStyle={{ color: 'hsl(var(--foreground))' }}
                        labelStyle={{ color: 'hsl(var(--foreground))' }}
                      />
                      <Bar dataKey="value" fill={chartColors.barSecondary} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No hay datos disponibles
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Pending Returns Widget */}
      {widgets.pendingReturns && (
        <Card data-testid="pending-returns-widget">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Equipos Pendientes de Retorno
              {pendingReturns?.critical_count > 0 && (
                <Badge variant="destructive" className="ml-2 animate-pulse" data-testid="overdue-critical-count">
                  {pendingReturns.critical_count} vencido(s)
                </Badge>
              )}
              {pendingReturns?.warning_count > 0 && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 ml-1" data-testid="overdue-warning-count">
                  {pendingReturns.warning_count} próximo(s)
                </Badge>
              )}
              {pendingReturns?.threshold_days && (
                <span className="ml-auto text-xs text-muted-foreground font-normal">
                  Umbral: {pendingReturns.threshold_days} días
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-32 w-full" />
            ) : (pendingReturns?.items || []).length > 0 ? (
              <div className="space-y-3">
                {(pendingReturns.items || []).map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between p-3 rounded-lg hover:brightness-110 transition-all cursor-pointer ${
                      item.alert_level === 'critical'
                        ? 'bg-red-500/10 border border-red-500/30'
                        : item.alert_level === 'warning'
                        ? 'bg-amber-500/10 border border-amber-500/30'
                        : 'bg-muted/30 border border-border/40'
                    }`}
                    onClick={() => navigate('/movements')}
                    data-testid={`pending-return-${item.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{item.event_name || 'Sin evento'}</p>
                        {item.type === 'prestamo' && (
                          <Badge className="bg-purple-500/20 text-purple-400 text-[10px]">Préstamo</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {item.responsible_name}{item.client_name ? ` · ${item.client_name}` : ''} · {item.total_items} equipo(s)
                      </p>
                      {item.item_names?.length > 0 && (
                        <p className="text-xs text-muted-foreground/70 truncate mt-0.5">
                          {item.item_names.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <Badge
                        variant={item.alert_level === 'critical' ? 'destructive' : item.alert_level === 'warning' ? 'warning' : 'secondary'}
                        className={item.alert_level === 'critical' ? 'animate-pulse' : ''}
                        data-testid={`days-badge-${item.id}`}
                      >
                        {item.days_out} días
                      </Badge>
                      {item.control_number && (
                        <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                          {item.control_number}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {pendingReturns.total > (pendingReturns.items || []).length && (
                  <p className="text-center text-sm text-muted-foreground pt-1">
                    +{pendingReturns.total - pendingReturns.items.length} más...
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                No hay equipos pendientes de retorno
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Low Stock Alerts Widget */}
      {widgets.lowStockAlerts && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Alertas de Stock Bajo
              {lowStockAlerts.length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {lowStockAlerts.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSecondary ? (
              <Skeleton className="h-32 w-full" />
            ) : lowStockAlerts.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {lowStockAlerts.slice(0, 10).map((item, idx) => (
                  <div 
                    key={item.equipment_id || idx} 
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/inventory?search=${encodeURIComponent(item.equipment_name)}`)}
                  >
                    <div className="flex-1">
                      <p className="font-medium">{item.equipment_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.category_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant={item.available === 0 ? "destructive" : item.available <= 2 ? "warning" : "secondary"}>
                        {item.available} / {item.total}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        Umbral: {item.threshold}
                      </p>
                    </div>
                  </div>
                ))}
                {lowStockAlerts.length > 10 && (
                  <p className="text-center text-sm text-muted-foreground pt-2">
                    +{lowStockAlerts.length - 10} más...
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                Todos los equipos tienen stock suficiente
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upcoming Reservations Widget */}
      {widgets.upcomingReservations && (
        <Card data-testid="upcoming-reservations-widget">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              Reservas Próximas
              {upcomingReservations.length > 0 && (
                <Badge className="bg-blue-500/20 text-blue-400 ml-2">
                  {upcomingReservations.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingPrimary ? (
              <Skeleton className="h-32 w-full" />
            ) : upcomingReservations.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {upcomingReservations.map((res, idx) => (
                  <div 
                    key={res.id || idx} 
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate('/reservations')}
                  >
                    <div className="flex-1">
                      <p className="font-medium">{res.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {res.responsible_name} {res.equipment_count > 0 ? `· ${res.equipment_count} equipo(s)` : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{res.start_date?.slice(0, 10)}</p>
                      <Badge className={res.status === 'confirmed' || res.status === 'confirmada' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                        {res.status === 'confirmed' || res.status === 'confirmada' ? 'Confirmada' : 'Pendiente'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                No hay reservas próximas esta semana
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Overdue Maintenance Widget */}
      {widgets.overdueMaintenance && (
        <Card data-testid="overdue-maintenance-widget">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="h-5 w-5 text-red-500" />
              Mantenimiento Pendiente
              {overdueMaintenance.filter(m => m.is_overdue).length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {overdueMaintenance.filter(m => m.is_overdue).length} vencido(s)
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingPrimary ? (
              <Skeleton className="h-32 w-full" />
            ) : overdueMaintenance.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {overdueMaintenance.map((m, idx) => (
                  <div 
                    key={m.id || idx} 
                    className={`flex items-center justify-between p-3 rounded-lg transition-colors cursor-pointer ${m.is_overdue ? 'bg-red-500/10 hover:bg-red-500/20 border border-red-500/20' : 'bg-muted/30 hover:bg-muted/50'}`}
                    onClick={() => navigate('/maintenance')}
                  >
                    <div className="flex-1">
                      <p className="font-medium">{m.equipment_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {m.type} · {m.description?.slice(0, 50)}{m.description?.length > 50 ? '...' : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{m.scheduled_date?.slice(0, 10)}</p>
                      <Badge className={
                        m.priority === 'critica' ? 'bg-red-500/20 text-red-400' :
                        m.priority === 'alta' ? 'bg-orange-500/20 text-orange-400' :
                        m.priority === 'media' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-green-500/20 text-green-400'
                      }>
                        {m.is_overdue ? 'Vencido' : m.priority}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                No hay mantenimientos pendientes o vencidos
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* P2: Active Faults Quick Action Widget */}
      {widgets.activeFaults && (
        <Card data-testid="active-faults-widget">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Unidades con Falla Activa
              {activeFaults.length > 0 && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 ml-2">
                  {activeFaults.length}
                </Badge>
              )}
              {activeFaults.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="ml-auto"
                  disabled={resolvingFaults}
                  data-testid="resolve-all-faults-btn"
                  onClick={async () => {
                    if (!window.confirm(`¿Resolver todas las ${activeFaults.length} fallas activas? Las unidades volverán a estado "Disponible".`)) return;
                    setResolvingFaults(true);
                    try {
                      const res = await api().post('/dashboard/resolve-all-faults');
                      toast.success(res.data.message || 'Fallas resueltas');
                      loadPrimaryData(true);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Error al resolver fallas');
                    } finally {
                      setResolvingFaults(false);
                    }
                  }}
                >
                  {resolvingFaults ? 'Resolviendo...' : 'Resolver Todas'}
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingPrimary ? (
              <Skeleton className="h-32 w-full" />
            ) : activeFaults.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {activeFaults.map((f, idx) => (
                  <div
                    key={f.id || idx}
                    className="flex items-center justify-between p-3 rounded-lg bg-amber-500/5 hover:bg-amber-500/10 border border-amber-500/15 transition-colors cursor-pointer"
                    onClick={() => navigate('/inventory')}
                  >
                    <div className="flex-1">
                      <p className="font-medium">{f.equipment_name} <span className="text-xs text-muted-foreground font-mono">#{f.serial_number}</span></p>
                      <p className="text-sm text-muted-foreground">{f.location_name}{f.fault_reason ? ` · ${f.fault_reason}` : ''}</p>
                    </div>
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Falla</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                No hay unidades con falla activa
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty state when all widgets hidden */}
      {!widgets.kpis && !widgets.kpis_secondary && !widgets.equipmentByStatus && 
       !widgets.movementsByMonth && !widgets.topEquipment && !widgets.recentMovements && 
       !widgets.upcomingEvents && !widgets.upcomingReservations && !widgets.overdueMaintenance &&
       !widgets.activeFaults && !widgets.equipmentByLocation && !widgets.equipmentByCategory &&
       !widgets.pendingReturns && !widgets.lowStockAlerts && (
        <Card className="py-12">
          <CardContent className="text-center">
            <EyeOff className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-medium text-lg mb-2">No hay widgets visibles</h3>
            <p className="text-muted-foreground mb-4">
              Todos los widgets están ocultos. Haz clic en "Personalizar" para mostrarlos.
            </p>
            <Button onClick={resetWidgets} variant="outline">
              Restaurar Widgets
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
